/* eslint-disable indent */

import React, { Fragment, useEffect, useRef } from 'react';
import { EpIcon } from '../EpIcon';

import './EpButton.scss';

interface EpButtonProps {
  buttonId?: string;
  buttonType:
  | 'primary'
  | 'primary-outline'
  | 'secondary'
  | 'secondary-outline'
  | 'danger-outline'
  | 'link-primary'
  | 'link-nav-primary'
  | 'link-secondary'
  | 'link-nav-secondary'
  | 'link-light';
  buttonSize?: 'sm' | 'md' | 'lg';
  buttonIsDisabled?: boolean;
  buttonIsActive?: boolean;
  customClasses?: string;
  buttonText?: string;
  buttonFormId?: string;
  buttonLeftIconName?: string;
  buttonRightIconName?: string;
  buttonActionType?: 'submit' | 'reset' | 'button';
  buttonDataBsToggle?: string;
  buttonAriaExpanded?: boolean;
  buttonDataBsAutoClose?: boolean;
  buttonDataBsTarget?: string;
  buttonAriaControls?: string;
  buttonDataBsDismiss?: string;
  buttonAriaLabel?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  buttonOnClickFunc?: any;
  buttonLinkHref?: string;
  buttonLinkTarget?: string;
  buttonLinkHasDownloadEnabled?: boolean;
  buttonIsFullWidth?: boolean;
  buttonIsFullHeight?: boolean;
  className?: string;
}

enum EpButtonStatusClasses {
  active = 'ep-button--active',
  disabled = 'ep-button--disabled',
  fullWidth = 'ep-button--full-width',
  fullHeight = 'ep-button--full-height',
}

export const EpButton: React.FC<EpButtonProps> = ({
  buttonId = '',
  buttonType,
  buttonSize = 'md',
  buttonIsDisabled = false,
  buttonIsActive = false,
  customClasses = '',
  buttonText = '',
  buttonFormId,
  buttonLeftIconName = '',
  buttonRightIconName = '',
  buttonActionType = 'button',
  buttonDataBsToggle = '',
  buttonAriaExpanded = false,
  buttonDataBsAutoClose = false,
  buttonDataBsTarget = '',
  buttonAriaControls = '',
  buttonDataBsDismiss = '',
  buttonAriaLabel = '',
  buttonOnClickFunc,
  buttonLinkHref = '',
  buttonLinkTarget = '_blank',
  buttonLinkHasDownloadEnabled = false,
  buttonIsFullWidth = false,
  buttonIsFullHeight = false,
  className = '',
}) => {
  const buttonIconSizeRef = useRef<number>();

  useEffect(() => {
    switch (buttonSize) {
      case 'sm':
        buttonIconSizeRef.current = 14;
        break;
      case 'md':
        buttonIconSizeRef.current = 16;
        break;
      case 'lg':
        buttonIconSizeRef.current = 20;
        break;
    }
  }, [buttonSize]);

  const getButtonContentStruture = () => {
    return (
      <Fragment>
        {buttonLeftIconName && (
          <div className="ep-button__icon ep-button__icon--start">
            <EpIcon
              iconName={buttonLeftIconName}
              iconSize={buttonIconSizeRef.current}
            />
          </div>
        )}

        {buttonText && <span className="ep-button__text">{buttonText}</span>}

        {buttonRightIconName && (
          <div className="ep-button__icon ep-button__icon--end">
            <EpIcon
              iconName={buttonRightIconName}
              iconSize={buttonIconSizeRef.current}
            />
          </div>
        )}
      </Fragment>
    );
  };

  const handleButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    if (!buttonLinkHref && buttonOnClickFunc) {
      event.preventDefault();
      buttonOnClickFunc(event);
    }
  };

  return (
    <div
      className={`
        ${buttonIsFullWidth ? EpButtonStatusClasses['fullWidth'] : ''}
        ${buttonIsFullHeight ? EpButtonStatusClasses['fullHeight'] : ''}
        ${className}
        `}
    >
      {buttonLinkHref ? (
        <a
          id={buttonId}
          className={`
            ep-button
            ep-button--${buttonType}
            ep-button--${buttonSize}
            ${buttonIsDisabled ? EpButtonStatusClasses['disabled'] : ''}
            ${buttonIsFullWidth ? EpButtonStatusClasses['fullWidth'] : ''}
            ${buttonIsFullHeight ? EpButtonStatusClasses['fullHeight'] : ''}
            ${buttonIsActive ? EpButtonStatusClasses['active'] : ''}
            ${buttonIsDisabled ? EpButtonStatusClasses['disabled'] : ''}
            ${customClasses}
          `}
          href={buttonLinkHref}
          target={buttonLinkTarget}
          rel="noopener noreferrer"
          download={buttonLinkHasDownloadEnabled ? '' : undefined}
        >
          {getButtonContentStruture()}
        </a>
      ) : (
        <button
          id={buttonId}
          type={buttonActionType}
          className={`
            ep-button
            ep-button--${buttonType}
            ep-button--${buttonSize}
            ${buttonIsDisabled ? EpButtonStatusClasses['disabled'] : ''}
            ${buttonIsFullWidth ? EpButtonStatusClasses['fullWidth'] : ''}
            ${buttonIsFullHeight ? EpButtonStatusClasses['fullHeight'] : ''}
            ${buttonIsActive ? EpButtonStatusClasses['active'] : ''}
            ${buttonIsDisabled ? EpButtonStatusClasses['disabled'] : ''}
            ${customClasses}
          `}
          form={buttonFormId}
          disabled={buttonIsDisabled}
          data-bs-toggle={buttonDataBsToggle}
          aria-expanded={buttonAriaExpanded}
          data-bs-auto-close={buttonDataBsAutoClose}
          data-bs-target={buttonDataBsTarget}
          data-bs-dismiss={buttonDataBsDismiss}
          aria-controls={buttonAriaControls}
          aria-label={buttonAriaLabel}
          onClick={handleButtonClick}
        >
          {getButtonContentStruture()}
        </button>
      )}
    </div>
  );
};
